/**
 * 📱 Componente de Conexão WhatsApp Pública
 * 
 * O QUE FAZ:
 * - Valida o token do link de conexão
 * - Exibe formulário para inserir número de telefone
 * - Mostra QR Code e código de pareamento
 * - Monitora status da conexão
 * 
 * ONDE É USADO:
 * - PublicWhatsAppConnectPage
 * 
 * COMO SE CONECTA:
 * - Usa edge function whatsapp-connection-link
 * - Não requer autenticação (token público)
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { ThinkingOrbLoader } from '@/components/ui/ThinkingOrbLoader';
import { 
  Loader2, 
  Smartphone, 
  QrCode, 
  CheckCircle2, 
  XCircle, 
  Copy, 
  RefreshCw,
  AlertTriangle,
  Clock
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface PublicWhatsAppConnectProps {
  token: string;
}

// Estados possíveis da página
type PageState = 'loading' | 'invalid' | 'expired' | 'used' | 'ready' | 'connecting' | 'qrcode' | 'success' | 'error';

export const PublicWhatsAppConnect = ({ token }: PublicWhatsAppConnectProps) => {
  // Estados do componente
  const [pageState, setPageState] = useState<PageState>('loading');
  const [clinicaNome, setClinicaNome] = useState<string>('');
  const [expiresAt, setExpiresAt] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  
  // Estados de conexão
  const [phoneInput, setPhoneInput] = useState<string>('');
  const [qrCode, setQrCode] = useState<string>('');
  const [pairingCode, setPairingCode] = useState<string>('');
  const [isPolling, setIsPolling] = useState(false);

  // Formatar número de telefone (DDI 55 + DDD + número)
  const formatPhoneNumber = (value: string): string => {
    // Remove todos os caracteres não numéricos
    let digits = value.replace(/\D/g, '');
    
    // Se começar com 0, remove
    if (digits.startsWith('0')) {
      digits = digits.substring(1);
    }
    
    // Adicionar DDI 55 se não tiver
    if (!digits.startsWith('55') && digits.length > 0) {
      digits = '55' + digits;
    }
    
    return digits;
  };

  // Validar token ao carregar a página
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setPageState('invalid');
        setErrorMessage('Link inválido - token não encontrado');
        return;
      }

      console.log('🔍 Validando token:', token.substring(0, 8) + '...');
      
      try {
        const { data, error } = await supabase.functions.invoke('whatsapp-connection-link', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          body: null,
        });

        // Como não podemos fazer GET com query params facilmente, 
        // vamos usar POST para validate também
        const response = await fetch(
          `https://wabnuxerjnecrjynyyfo.supabase.co/functions/v1/whatsapp-connection-link/validate?token=${token}`,
          { method: 'GET', headers: { 'Content-Type': 'application/json' } }
        );

        const result = await response.json();
        console.log('📥 Resposta validação:', result);

        if (!response.ok || !result.valid) {
          if (response.status === 410 || result.error?.includes('expirado')) {
            setPageState('expired');
            setErrorMessage('Este link expirou. Solicite um novo link ao administrador.');
          } else if (result.error?.includes('utilizado')) {
            setPageState('used');
            setErrorMessage('Este link já foi utilizado. Solicite um novo link se necessário.');
          } else {
            setPageState('invalid');
            setErrorMessage(result.error || 'Link inválido');
          }
          return;
        }

        // Token válido
        setClinicaNome(result.clinica_nome || 'Clínica');
        setExpiresAt(result.expires_at);
        setPageState('ready');
        
      } catch (err) {
        console.error('❌ Erro ao validar token:', err);
        setPageState('error');
        setErrorMessage('Erro ao validar link. Tente novamente.');
      }
    };

    validateToken();
  }, [token]);

  // Iniciar conexão WhatsApp
  const handleConnect = async () => {
    const formattedPhone = formatPhoneNumber(phoneInput);
    
    if (formattedPhone.length < 12) {
      toast.error('Digite um número de telefone válido com DDD');
      return;
    }

    setPageState('connecting');
    console.log('📱 Iniciando conexão com telefone:', formattedPhone);

    try {
      const response = await fetch(
        'https://wabnuxerjnecrjynyyfo.supabase.co/functions/v1/whatsapp-connection-link/connect',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, phone: formattedPhone })
        }
      );

      const result = await response.json();
      console.log('📥 Resposta conexão:', result);

      if (!response.ok) {
        throw new Error(result.error || 'Erro ao conectar');
      }

      // Exibir QR Code e código de pareamento
      if (result.qrcode) {
        setQrCode(result.qrcode);
      }
      if (result.pairingCode) {
        setPairingCode(result.pairingCode);
      }
      
      setPageState('qrcode');
      
      // Iniciar polling de status
      startPolling();

    } catch (err: any) {
      console.error('❌ Erro ao conectar:', err);
      setPageState('error');
      setErrorMessage(err.message || 'Erro ao iniciar conexão');
    }
  };

  // Verificar status da conexão
  const checkStatus = useCallback(async () => {
    try {
      const response = await fetch(
        `https://wabnuxerjnecrjynyyfo.supabase.co/functions/v1/whatsapp-connection-link/status?token=${token}`,
        { method: 'GET' }
      );

      const result = await response.json();
      console.log('📊 Status:', result);

      if (result.connected) {
        setPageState('success');
        setIsPolling(false);
        toast.success('WhatsApp conectado com sucesso!');
      }

      return result.connected;
    } catch (err) {
      console.error('Erro ao verificar status:', err);
      return false;
    }
  }, [token]);

  // Polling de status
  const startPolling = useCallback(() => {
    setIsPolling(true);
    
    const pollInterval = setInterval(async () => {
      const connected = await checkStatus();
      if (connected) {
        clearInterval(pollInterval);
      }
    }, 3000); // Verificar a cada 3 segundos

    // Parar após 5 minutos
    setTimeout(() => {
      clearInterval(pollInterval);
      setIsPolling(false);
    }, 5 * 60 * 1000);
  }, [checkStatus]);

  // Copiar código de pareamento
  const copyPairingCode = () => {
    if (pairingCode) {
      navigator.clipboard.writeText(pairingCode);
      toast.success('Código copiado!');
    }
  };

  // Gerar novo QR Code
  const refreshQrCode = () => {
    setPageState('ready');
    setQrCode('');
    setPairingCode('');
  };

  // Calcular tempo restante
  const getTimeRemaining = () => {
    if (!expiresAt) return '';
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires.getTime() - now.getTime();
    if (diff <= 0) return 'Expirado';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}min restantes`;
  };

  // ============================================
  // RENDERIZAÇÃO DOS ESTADOS
  // ============================================

  // Estado: Carregando
  if (pageState === 'loading') {
    return (
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <ThinkingOrbLoader state="searching" size={64} label="Validando link..." />
        </CardContent>
      </Card>
    );
  }

  // Estado: Token inválido
  if (pageState === 'invalid') {
    return (
      <Card className="w-full max-w-md border-destructive">
        <CardHeader className="text-center">
          <XCircle className="h-16 w-16 text-destructive mx-auto mb-2" />
          <CardTitle className="text-destructive">Link Inválido</CardTitle>
          <CardDescription>{errorMessage}</CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          <p className="text-sm text-muted-foreground">
            Entre em contato com o administrador para obter um novo link de conexão.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Estado: Token expirado
  if (pageState === 'expired') {
    return (
      <Card className="w-full max-w-md border-amber-500">
        <CardHeader className="text-center">
          <Clock className="h-16 w-16 text-amber-500 mx-auto mb-2" />
          <CardTitle className="text-amber-600">Link Expirado</CardTitle>
          <CardDescription>{errorMessage}</CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          <p className="text-sm text-muted-foreground">
            Os links de conexão são válidos por 24 horas. Solicite um novo link ao administrador.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Estado: Token já usado
  if (pageState === 'used') {
    return (
      <Card className="w-full max-w-md border-blue-500">
        <CardHeader className="text-center">
          <CheckCircle2 className="h-16 w-16 text-blue-500 mx-auto mb-2" />
          <CardTitle className="text-blue-600">Link Já Utilizado</CardTitle>
          <CardDescription>{errorMessage}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  // Estado: Erro
  if (pageState === 'error') {
    return (
      <Card className="w-full max-w-md border-destructive">
        <CardHeader className="text-center">
          <AlertTriangle className="h-16 w-16 text-destructive mx-auto mb-2" />
          <CardTitle className="text-destructive">Erro</CardTitle>
          <CardDescription>{errorMessage}</CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          <Button onClick={() => window.location.reload()} variant="outline">
            <RefreshCw className="h-4 w-4 mr-2" />
            Tentar Novamente
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Estado: Sucesso
  if (pageState === 'success') {
    return (
      <Card className="w-full max-w-md border-green-500">
        <CardHeader className="text-center">
          <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-2" />
          <CardTitle className="text-green-600">WhatsApp Conectado!</CardTitle>
          <CardDescription>
            A conexão foi realizada com sucesso para {clinicaNome}.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-center">
          <p className="text-sm text-muted-foreground">
            Você pode fechar esta página. O WhatsApp está pronto para uso no sistema.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Estado: Pronto para conectar
  if (pageState === 'ready') {
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <Smartphone className="h-12 w-12 text-primary mx-auto mb-2" />
          <CardTitle>Conectar WhatsApp</CardTitle>
          <CardDescription>
            Conectando WhatsApp para <span className="font-semibold">{clinicaNome}</span>
          </CardDescription>
          <Badge variant="secondary" className="mt-2">
            <Clock className="h-3 w-3 mr-1" />
            {getTimeRemaining()}
          </Badge>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="phone">Número do WhatsApp</Label>
            <Input
              id="phone"
              type="tel"
              placeholder="11999999999"
              value={phoneInput}
              onChange={(e) => setPhoneInput(e.target.value)}
              className="text-lg"
            />
            <p className="text-xs text-muted-foreground">
              Digite o DDD + número (ex: 11999999999)
            </p>
          </div>

          <Button 
            onClick={handleConnect} 
            className="w-full"
            size="lg"
            disabled={!phoneInput}
          >
            <QrCode className="h-5 w-5 mr-2" />
            Gerar QR Code
          </Button>

          <Alert>
            <Smartphone className="h-4 w-4" />
            <AlertTitle>Como funciona</AlertTitle>
            <AlertDescription className="text-sm">
              1. Digite o número do WhatsApp<br />
              2. Escaneie o QR Code ou use o código de pareamento<br />
              3. Aguarde a confirmação de conexão
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  // Estado: Conectando
  if (pageState === 'connecting') {
    return (
      <Card className="w-full max-w-md">
        <CardContent className="flex flex-col items-center justify-center py-12">
          <ThinkingOrbLoader state="working" size={64} label="Gerando QR Code..." />
        </CardContent>
      </Card>
    );
  }

  // Estado: Exibindo QR Code
  if (pageState === 'qrcode') {
    return (
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>Escaneie o QR Code</CardTitle>
          <CardDescription>
            Use o WhatsApp no celular para escanear
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* QR Code */}
          {qrCode && (
            <div className="flex justify-center">
              <div className="p-4 bg-white rounded-lg shadow-inner">
                <img 
                  src={`data:image/png;base64,${qrCode}`} 
                  alt="QR Code"
                  className="w-64 h-64"
                />
              </div>
            </div>
          )}

          {/* Código de Pareamento */}
          {pairingCode && (
            <div className="space-y-2">
              <Label className="text-center block">Ou use o código de pareamento:</Label>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-center text-2xl font-mono bg-muted p-3 rounded-lg tracking-widest">
                  {pairingCode}
                </code>
                <Button variant="outline" size="icon" onClick={copyPairingCode}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                WhatsApp → Configurações → Dispositivos Conectados → Conectar Dispositivo → Usar código
              </p>
            </div>
          )}

          {/* Status de polling */}
          {isPolling && (
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Aguardando conexão...
            </div>
          )}

          {/* Botões de ação */}
          <div className="flex gap-2">
            <Button variant="outline" onClick={refreshQrCode} className="flex-1">
              <RefreshCw className="h-4 w-4 mr-2" />
              Novo QR Code
            </Button>
            <Button variant="outline" onClick={checkStatus} className="flex-1">
              Verificar Status
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return null;
};
